<template>
	<div class="container-fluid px-4 pt-6 pb-10">
		<!-- Same columns as the content: title and calendar tabs over the calendar, the Lucrari link
		     over the "De programat" panel. -->
		<div class="calendar-layout calendar-header py-2 mb-4">
			<div class="calendar-main flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
				<h1 class="page-title">
					<v-icon large class="mr-1">{{ calendar.icon }}</v-icon>
					{{ calendar.title }}
				</h1>
				<div class="flex flex-row flex-wrap items-center justify-end gap-2">
					<v-switch v-if="type === 'montaj'" v-model="showDeadlines" class="mt-0 mr-4 flex-grow-0"
						label="Arata termene" hide-details inset @change="refetch"></v-switch>
					<v-btn-toggle :value="type" mandatory background-color="white" color="primary" class="calendar-tabs">
						<v-btn v-for="cal in calendars" :key="cal.type" :value="cal.type" color="white"
							:to="'/lucrari/calendar/' + cal.type">
							<v-icon left>{{ cal.icon }}</v-icon>
							{{ cal.type }}
						</v-btn>
					</v-btn-toggle>
				</div>
			</div>
			<div class="calendar-side flex flex-row justify-end">
				<v-btn color="blue-grey" class="uppercase" dark to="/lucrari">
					<v-icon left>mdi-format-list-bulleted</v-icon>
					Lucrari
				</v-btn>
			</div>
		</div>

		<div class="calendar-layout">
			<div class="calendar-main">
				<v-card class="p-4">
					<FullCalendar ref="calendar" :options="calendarOptions">
						<template v-slot:eventContent="arg">
							<div class="work-event" :class="{ 'work-event--late': arg.event.extendedProps.late }">
								<div class="work-event__title">
									<span v-if="arg.timeText" class="mr-1">{{ arg.timeText }}</span>
									<v-icon v-if="arg.event.extendedProps.late" x-small color="red">mdi-alert</v-icon>
									<v-icon v-if="arg.event.extendedProps.noFinisaje" x-small color="orange"
										title="Finisaje neconfirmate">mdi-wall-fire</v-icon>
									<b>{{ arg.event.title }}</b>
									<span v-if="arg.event.extendedProps.hoursText" class="ml-1">
										({{ arg.event.extendedProps.hoursText }})</span>
								</div>
								<div v-if="arg.event.extendedProps.valueText" class="work-event__value"
									:title="arg.event.extendedProps.valueTitle">{{ arg.event.extendedProps.valueText }}</div>
								<div v-if="arg.event.extendedProps.assignees" class="work-event__team">
									<span v-for="a in arg.event.extendedProps.assignees" :key="a.type + a.id"
										class="inline-flex items-center mr-2">
										<span v-if="a.color" class="dot mr-1" :class="a.color"></span>{{ a.name }}
									</span>
								</div>
							</div>
						</template>
					</FullCalendar>
				</v-card>

				<!-- Production week: what each day is worth and how many hours it carries -->
				<v-card v-if="weekTotals" class="p-4 mt-4">
					<h2 class="text-lg font-bold mb-2">Totaluri saptamana</h2>
					<div class="overflow-x-auto">
						<table class="day-totals">
							<thead>
								<tr>
									<th></th>
									<th v-for="d in weekTotals.days" :key="d.date">{{ d.label }}</th>
									<th>Total</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td title="Module gard, fara TVA, la cursul EUR din ziua contractului">Valoare</td>
									<td v-for="d in weekTotals.days" :key="d.date">{{ d.ron ? formatRon(d.ron, 1) : "-" }}</td>
									<td class="font-bold">{{ formatRon(weekTotals.ron, 1) }}</td>
								</tr>
								<tr>
									<td>Ore</td>
									<td v-for="d in weekTotals.days" :key="d.date">{{ d.hours ? formatHours(d.hours) : "-" }}</td>
									<td class="font-bold">{{ formatHours(weekTotals.hours) }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</v-card>
			</div>

			<div class="calendar-side">
				<!-- Works waiting for this calendar -->
				<v-card class="p-4">
					<div class="flex flex-row justify-between items-center mb-2">
						<h2 class="text-lg font-bold">De programat</h2>
						<v-chip small>{{ toSchedule.length }}</v-chip>
					</div>
					<v-progress-linear v-if="loadingWorks" indeterminate class="mb-2" />
					<p v-if="!loadingWorks && !toSchedule.length" class="text-sm text-gray-600">
						Nicio lucrare de programat.
					</p>
					<v-list dense class="p-0">
						<v-list-item v-for="w in toSchedule" :key="w.id_work" class="px-0">
							<v-list-item-content>
								<v-list-item-title class="font-bold">{{ w.client_nume }}</v-list-item-title>
								<v-list-item-subtitle>
									Contract {{ w.contract_id }} &middot; {{ w.client_oras }}
								</v-list-item-subtitle>
								<v-list-item-subtitle v-if="type === 'masuratori' && !finisajeDone(w)" class="orange--text">
									<v-icon x-small color="orange">mdi-wall-fire</v-icon> Finisaje neconfirmate
								</v-list-item-subtitle>
								<v-list-item-subtitle v-if="w.data_montaj_maxima">
									Montaj maxim: {{ w.data_montaj_maxima | formatDate }}
								</v-list-item-subtitle>
							</v-list-item-content>
							<v-list-item-action v-if="canEdit">
								<v-btn icon color="teal" title="Programeaza" @click="openCreate(w)">
									<v-icon>mdi-calendar-plus</v-icon>
								</v-btn>
							</v-list-item-action>
						</v-list-item>
					</v-list>
				</v-card>
			</div>
		</div>

		<WorkEventDialog v-model="dialog.open" :type="type" :event="dialog.event" :works="bookableWorks"
			:fixed-work="dialog.work" :selection="dialog.selection" :read-only="dialog.readOnly"
			:can-open-work="!dialog.event || !isAgent || isAgentsWork(user, dialog.event)" @saved="onSaved" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import roLocale from "@fullcalendar/core/locales/ro";
import { CALENDARS, canBook, stageInfo, workSchedule, formatHours, fenceValueNet, formatRon, finisajeDone, isWorksAgent, isAgentsWork, agentFilters, agentCanChange, isAmanata } from "@/utils/works";
import WorkEventDialog from "./WorkEventDialog";

const WEEKDAYS = ["Dum", "Lun", "Mar", "Mie", "Joi", "Vin", "Sam"];

// Event colours by work stage (Vuetify palette, light enough for black text).
const STAGE_BG = {
	asteapta_confirmare_finisaje: "#d7ccc8",
	asteapta_masuratori: "#ffe0b2",
	gata_productie: "#bbdefb",
	programat_productie: "#c5cae9",
	pregatit_montaj: "#d1c4e9",
	programat_montaj: "#b2dfdb",
	finalizata: "#c8e6c9",
	platita: "#a5d6a7",
};

export default {
	name: "WorkCalendar",
	components: { FullCalendar, WorkEventDialog },
	props: {
		type: { type: String, required: true },
	},
	data() {
		return {
			showDeadlines: false,
			dialog: { open: false, event: null, work: null, selection: null, readOnly: false },
			// what the calendar shows now, for the totals under it
			view: null,
			pieces: [],
			calendarOptions: this.buildOptions(),
		};
	},
	computed: {
		...mapState("works", ["works", "loadingWorks", "settings"]),
		calendar() {
			return CALENDARS[this.type];
		},
		user() {
			return this.$store.getters.loggedUser;
		},
		isAgent() {
			return isWorksAgent(this.user);
		},
		// the work's value on production events and the day totals under the calendar
		canSeeValue() {
			return ["admin", "superuser"].includes(this.user.rol);
		},
		// Everyone sees the three calendars with every booking, so an agent can tell the client when
		// a measurement or montage is possible; agents change only their works' measurements.
		calendars() {
			return Object.values(CALENDARS);
		},
		// Agents book measurements; production staff (and admins) book everything.
		canEdit() {
			if (this.type === "masuratori") return ["admin", "superuser", "user", "agent"].includes(this.user.rol);
			return !this.isAgent && ["admin", "superuser", "user"].includes(this.user.rol);
		},
		ownWorks() {
			return this.isAgent ? this.works.filter((w) => isAgentsWork(this.user, w)) : this.works;
		},
		bookableWorks() {
			return this.ownWorks.filter((w) => canBook(w, this.type) && (!this.isAgent || agentCanChange(w)));
		},
		// Per visible day of the production week view: value and hours of the pieces on it.
		weekTotals() {
			if (!this.canSeeValue || this.type !== "productie" || !this.view || this.view.type !== "timeGridWeek") return null;
			const hidden = this.calendarOptions.hiddenDays || [];
			const days = [];
			for (let d = moment(this.view.start); d.isBefore(this.view.end); d.add(1, "day")) {
				if (hidden.includes(d.day())) continue;
				const date = d.format("YYYY-MM-DD");
				const onDay = this.pieces.filter((p) => p.date === date);
				days.push({
					date,
					label: WEEKDAYS[d.day()] + " " + d.format("D.MM"),
					ron: onDay.reduce((sum, p) => sum + p.ron, 0),
					hours: onDay.reduce((sum, p) => sum + p.hours, 0),
				});
			}
			return {
				days,
				ron: days.reduce((sum, d) => sum + d.ron, 0),
				hours: days.reduce((sum, d) => sum + d.hours, 0),
			};
		},
		toSchedule() {
			return this.ownWorks.filter((w) => !isAmanata(w) && this.calendar.toSchedule(w));
		},
	},
	watch: {
		// the RON value on production events needs the EUR rate, which may land after the events
		// (only for contracts older than the rate history, which fall back to today's rate)
		"$store.getters.curs"() {
			this.refetch();
		},
		// Same component instance when switching calendars from the toggle. The new options update
		// the toolbar and editing rules, but the events source is the same function and initialView
		// only applies on creation, so switch the view and reload the events by hand.
		type() {
			this.calendarOptions = this.buildOptions();
			this.$nextTick(() => {
				const api = this.api();
				if (!api) return;
				api.changeView(this.calendarOptions.initialView);
				api.refetchEvents();
			});
		},
		// The production view follows the working day, which arrives after the first render.
		settings() {
			this.calendarOptions = this.buildOptions();
		},
	},
	methods: {
		isAgentsWork,
		finisajeDone,
		formatRon,
		formatHours,
		buildOptions() {
			const mode = CALENDARS[this.type].mode;
			const schedule = workSchedule(this.$store.state.works.settings);
			const hours = mode === "hours";
			return {
				plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
				locale: roLocale,
				initialView: hours ? "timeGridWeek" : "dayGridMonth",
				headerToolbar: {
					left: "prev,next today",
					center: "title",
					right: hours ? "dayGridMonth,timeGridWeek,timeGridDay" : "dayGridMonth,dayGridWeek",
				},
				firstDay: 1,
				// production follows its working days; measurements and montage show the whole week
				hiddenDays: hours ? schedule.hiddenDays : [],
				height: "auto",
				allDaySlot: !hours,
				slotMinTime: schedule.startTime,
				slotMaxTime: schedule.endTime,
				businessHours: hours
					? { daysOfWeek: schedule.days.map((d) => d % 7), startTime: schedule.startTime, endTime: schedule.endTime }
					: false,
				nowIndicator: true,
				dayMaxEvents: false,
				displayEventEnd: true,
				editable: true,
				// production length is its hours (changed in the dialog); measurements are one day
				eventDurationEditable: mode === "range",
				selectable: true,
				selectMirror: true,
				eventColor: CALENDARS[this.type].color,
				eventTextColor: "#000",
				events: this.loadEvents,
				select: this.onSelect,
				eventClick: this.onEventClick,
				eventDrop: this.onEventMove,
				eventResize: this.onEventMove,
				eventAllow: this.eventAllow,
				datesSet: this.onDatesSet,
				eventsSet: this.onEventsSet,
			};
		},
		api() {
			return this.$refs.calendar && this.$refs.calendar.getApi();
		},
		refetch() {
			const api = this.api();
			if (api) api.refetchEvents();
		},
		loadWorks() {
			const filters = this.isAgent ? agentFilters(this.user) : {};
			return this.$store.dispatch("works/fetchWorks", filters).catch(() => {});
		},
		// FullCalendar asks for each visible range.
		loadEvents(info, success, failure) {
			this.$store
				.dispatch("works/fetchEvents", {
					type: this.type,
					from: moment(info.start).format("YYYY-MM-DD HH:mm:ss"),
					to: moment(info.end).format("YYYY-MM-DD HH:mm:ss"),
					// every booking, an agent's colleagues' too: they see when there is room
				})
				.then((events) => success([].concat(...events.map(this.toCalendarEvents), this.deadlineMarkers(info))))
				.catch(failure);
		},
		// One calendar event per booking, or one per working day for a production job, grouped so
		// that dragging any piece moves the whole job.
		toCalendarEvents(e) {
			const allDay = e.all_day == 1;
			// a montage that ends after the work's deadline
			const lastDay = moment(e.end).subtract(1, "minute").format("YYYY-MM-DD");
			const late = this.type === "montaj" && !!e.data_montaj_maxima && lastDay > e.data_montaj_maxima;
			const base = {
				groupId: String(e.id_event),
				title: this.eventTitle(e),
				allDay,
				backgroundColor: STAGE_BG[e.stage] || "#eceff1",
				borderColor: late ? "#e53935" : CALENDARS[this.type].color,
				editable: this.canEditEvent(e),
				extendedProps: {
					raw: e,
					assignees: e.assignees,
					late,
					// a measurement booked while the client's finishings aren't done yet
					noFinisaje: this.type === "masuratori" && ["contractat", "asteapta_confirmare_finisaje", "asteapta_masuratori"].includes(e.stage) && !finisajeDone(e),
					stage: stageInfo(e.stage).text,
				},
			};
			if (e.segments && e.segments.length) {
				const value = fenceValueNet(e);
				// the rate on the contract's day; today's for contracts older than the rate history
				const contractCurs = Number(e.curs_contract);
				const curs = contractCurs || Number(this.$store.getters.curs);
				const cursText = contractCurs ? "la cursul EUR din ziua contractului" : "la cursul EUR curent";
				const withValue = value != null && this.canSeeValue && curs;
				// each day's piece carries its share of the value, by the working hours it covers
				// (the pieces are cut to working hours, so their lengths add up to the job's hours)
				const pieceHours = e.segments.map((seg) => moment(seg.end).diff(moment(seg.start), "minutes") / 60);
				const totalHours = pieceHours.reduce((sum, h) => sum + h, 0);
				return e.segments.map((seg, i) => ({
					...base,
					id: `${e.id_event}-${i}`,
					start: seg.start,
					end: seg.end,
					extendedProps: {
						...base.extendedProps,
						// total hours on the first piece only
						hoursText: i === 0 ? formatHours(e.hours) : null,
						valueText: withValue && totalHours ? formatRon((value * pieceHours[i]) / totalHours, curs) : null,
						// for the day totals
						valueRon: withValue && totalHours ? (value * curs * pieceHours[i]) / totalHours : 0,
						pieceHours: pieceHours[i],
						valueTitle: e.segments.length > 1
							? `Module gard, fara TVA: ${Math.round((pieceHours[i] / totalHours) * 100)}% din ${formatRon(value, curs)}, ${cursText}`
							: `Module gard, fara TVA, ${cursText}`,
					},
				}));
			}
			return [{
				...base,
				id: String(e.id_event),
				start: allDay ? e.start.substr(0, 10) : e.start,
				end: allDay ? e.end.substr(0, 10) : e.end,
			}];
		},
		// Client, and for the teams that go on site (measurements, montage) the site's county.
		// No city: the client's may not be where the montage is.
		eventTitle(e) {
			if (this.type === "productie") return e.client_nume + (e.client_oras ? " - " + e.client_oras : "");
			return e.client_nume + (e.judet ? " - " + e.judet : "");
		},
		// Montage deadlines in view, as markers on the day, for works not yet done.
		deadlineMarkers(info) {
			if (this.type !== "montaj" || !this.showDeadlines) return [];
			const from = moment(info.start).format("YYYY-MM-DD");
			const to = moment(info.end).format("YYYY-MM-DD");
			return this.works
				.filter((w) => w.data_montaj_maxima && w.data_montaj_maxima >= from && w.data_montaj_maxima < to)
				.map((w) => ({
					id: "deadline-" + w.id_work,
					title: "Termen: " + w.client_nume,
					start: w.data_montaj_maxima,
					allDay: true,
					editable: false,
					backgroundColor: "#ffebee",
					borderColor: "#ffebee",
					textColor: "#c62828",
					classNames: ["deadline-marker"],
					extendedProps: { deadlineWork: w },
				}));
		},
		// Agents may only change measurement bookings of their works, before production.
		canEditEvent(e) {
			return this.canEdit && (!this.isAgent || (isAgentsWork(this.user, e) && agentCanChange(e)));
		},
		eventAllow() {
			return this.canEdit;
		},
		onDatesSet(info) {
			this.view = { type: info.view.type, start: info.start, end: info.end };
		},
		// Plain copies of the production pieces on screen; FullCalendar's objects stay out of Vue.
		onEventsSet(events) {
			this.pieces = events
				.filter((ev) => ev.extendedProps.pieceHours)
				.map((ev) => ({
					date: moment(ev.start).format("YYYY-MM-DD"),
					ron: ev.extendedProps.valueRon,
					hours: ev.extendedProps.pieceHours,
				}));
		},
		openCreate(work = null, selection = null) {
			this.dialog = { open: true, event: null, work, selection, readOnly: false };
		},
		onSelect(info) {
			if (this.canEdit) {
				this.openCreate(null, { start: info.start, end: info.end, allDay: info.allDay });
			}
			const api = this.api();
			if (api) api.unselect();
		},
		onEventClick(info) {
			const props = info.event.extendedProps;
			if (props.deadlineWork) {
				this.$router.push("/lucrari/" + props.deadlineWork.id_work);
				return;
			}
			this.dialog = {
				open: true,
				event: props.raw,
				work: null,
				selection: null,
				readOnly: !this.canEditEvent(props.raw),
			};
		},
		// Drag or resize: save the new dates, put the event back if the API refuses.
		async onEventMove(info) {
			const raw = info.event.extendedProps.raw;
			const mode = this.calendar.mode;
			let payload;
			if (mode === "hours") {
				// The piece dragged may not be the first one: move the job's start by the same amount
				// and keep its hours; the API works out the new end.
				const d = info.delta;
				const start = moment(raw.start)
					.add(d.years, "years")
					.add(d.months, "months")
					.add(d.days, "days")
					.add(d.milliseconds, "milliseconds");
				payload = { start: start.format("YYYY-MM-DD HH:mm:ss") };
			} else if (mode === "day") {
				payload = { start: moment(info.event.start).format("YYYY-MM-DD") };
			} else {
				const e = info.event;
				// FullCalendar drops the end of an event that lasts exactly its default duration
				const end = e.end || moment(e.start).add(1, "day").toDate();
				payload = { start: moment(e.start).format("YYYY-MM-DD"), end: moment(end).format("YYYY-MM-DD") };
			}
			try {
				await this.$store.dispatch("works/updateEvent", { id: raw.id_event, ...payload });
				this.refetch();
			} catch (err) {
				info.revert();
			}
		},
		async onSaved() {
			await this.loadWorks();
			this.refetch();
		},
	},
	async created() {
		this.$store.dispatch("works/fetchSettings").catch(() => {});
		this.$store.dispatch("fetchCurrency");
		await this.loadWorks();
		// deadline markers come from the works list, so draw them once it is in
		this.refetch();
	},
};
</script>

<style>
.work-event {
	padding: 1px 3px;
	white-space: normal;
	overflow: hidden;
	font-size: 0.8rem;
	line-height: 1.2;
}
.work-event__team,
.work-event__value {
	font-size: 0.75rem;
}
.work-event .dot {
	border-radius: 100px;
	width: 9px;
	height: 9px;
	display: inline-block;
}
/* Calendar takes the remaining width; the "De programat" panel is fixed on the right. Below
   1280px the panel drops under the calendar. */
.calendar-layout {
	display: flex;
	flex-direction: column;
	gap: 16px;
}
.calendar-main {
	flex: 1 1 auto;
	min-width: 0;
}
@media (min-width: 1280px) {
	.calendar-layout {
		flex-direction: row;
		align-items: flex-start;
	}
	.calendar-side {
		flex: 0 0 320px;
		width: 320px;
	}
	/* the header row centres its two parts vertically */
	.calendar-header.calendar-layout {
		align-items: center;
	}
}
.calendar-tabs .v-btn.v-btn {
	height: 44px;
	padding: 0 18px;
	font-size: 0.95rem;
	letter-spacing: 0.04em;
}
.day-totals {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.85rem;
}
.day-totals th,
.day-totals td {
	padding: 4px 8px;
	text-align: right;
	white-space: nowrap;
	border-bottom: 1px solid #eceff1;
}
.day-totals th:first-child,
.day-totals td:first-child {
	text-align: left;
	font-weight: 600;
}
.deadline-marker .work-event__title b {
	font-weight: 600;
}
</style>
